import React, { useEffect, useMemo, useState } from 'react';
import { apiClient, useEntityData } from 'c-data';
import { Alert, AutoGrid } from 'c-components';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import { Box, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { ReportDateTimeframe } from 'c-reports/Types';
import type { PopDisplays_ChannelFrame } from 'c-sdk';
import { format, isBefore, subMonths } from 'date-fns';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import { LogFileParsedDaily } from 'c-pop-displays/Components/LogFileParsed';
import { channelFrameMediaBreakdownFetcher } from 'c-pop-displays/Lib/channelFrameMediaBreakdown';
import { ReportDateSwitcher } from 'c-reports/Components';

import DisplayDetailsTable from '../Display/DisplayDetailsTable';
import MediaSummaryBreakdownTable from '../MediaSummaryBreakdownTable';

type Props = {
    id: number;
};

const ChannelFrameDetails: React.FC<Props> = ({ id }) => {
    const { getById } = useEntityData<PopDisplays_ChannelFrame>('PopDisplays_ChannelFrame');
    const channelFrame = getById({ id });
    const { parseApiDayMonthYearHourMinuteSecondFormat } = useDateUtils();

    const minDate = useMemo(
        () =>
            channelFrame.first_seen
                ? parseApiDayMonthYearHourMinuteSecondFormat(channelFrame.first_seen)
                : new Date(),
        [channelFrame.first_seen, parseApiDayMonthYearHourMinuteSecondFormat],
    );
    const maxDate = useMemo(
        () =>
            channelFrame.last_seen
                ? parseApiDayMonthYearHourMinuteSecondFormat(channelFrame.last_seen)
                : new Date(),
        [channelFrame.last_seen, parseApiDayMonthYearHourMinuteSecondFormat],
    );

    const initialStart = useMemo(() => {
        const twelveMonthsAgo = subMonths(maxDate, 12);
        return isBefore(twelveMonthsAgo, minDate) ? minDate : twelveMonthsAgo;
    }, [minDate, maxDate]);

    const [mbTimeframe, setMbTimeframe] = useState<ReportDateTimeframe>(ReportDateTimeframe.Range);
    const [mbStart, setMbStart] = useState<Date>(initialStart);
    const [mbEnd, setMbEnd] = useState<Date>(maxDate);

    const fetchData = useMemo(() => channelFrameMediaBreakdownFetcher(id, []), [id]);
    const { start, data, error, isLoading, reset } = useAPIClientRequest(
        apiClient.PopDisplays.channelFrameMediaBreakdown,
    );

    useEffect(() => {
        if (!mbStart || !mbEnd) return;
        start(id, {
            start_date: `${format(mbStart, 'yyyy-MM-dd')} 00:00:00`,
            end_date: `${format(mbEnd, 'yyyy-MM-dd')} 23:59:59`,
            media_items: [],
        });
    }, [id, start, mbStart, mbEnd]);

    if (!channelFrame) {
        return null;
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={12} xl>
                    <AutoGrid spacing={0} xs={12}>
                        <KV title="Modules.PopDisplays.ChannelFrame.table.idCol" value={id} />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.table.sourceCol"
                            value={channelFrame.source}
                        />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.table.frameCol"
                            value={channelFrame.frame}
                        />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.table.frameExtIdCol"
                            value={channelFrame.frame_extid}
                        />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.table.playerCol"
                            value={channelFrame.player}
                        />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.table.playerExtIdCol"
                            value={channelFrame.player_extid}
                        />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.table.uuidCol"
                            value={channelFrame.uuid}
                        />

                        <KV
                            title="Modules.PopDisplays.ChannelFrame.details.minDateLabel"
                            value={channelFrame.first_seen}
                        />
                        <KV
                            title="Modules.PopDisplays.ChannelFrame.details.maxDateLabel"
                            value={channelFrame.last_seen}
                        />
                    </AutoGrid>
                </Grid>
                <Grid item xs={12} lg={12} xl>
                    {Array.isArray(channelFrame.displays) && (
                        <>
                            <Typography variant="h3">
                                <Translate path="Modules.PopDisplays.ChannelFrame.details.displaysHeader" />
                            </Typography>
                            <DisplayDetailsTable displays={channelFrame.displays} id={id} />
                        </>
                    )}
                </Grid>
            </Grid>

            <Box mt={2}>
                <Card>
                    <CardHeader
                        title={
                            <Typography variant="h3">
                                <Translate path="Modules.PopDisplays.ChannelFrame.details.dateBreakdownHeader" />
                            </Typography>
                        }
                    />

                    <CardContent>
                        <Box height="75vh">
                            <LogFileParsedDaily
                                minDate={minDate}
                                maxDate={maxDate}
                                chartName={`${id}-daily-totals`}
                                fetchData={fetchData}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
            {error && (
                <Alert severity="error" onClose={reset}>
                    {error}
                </Alert>
            )}

            {channelFrame.first_seen && channelFrame.last_seen && (
                <Box mt={2}>
                    <Card>
                        <CardHeader
                            title={
                                <Typography variant="h3">
                                    <Translate path="Modules.PopDisplays.ChannelFrame.details.mediaBreakdownHeader" />
                                </Typography>
                            }
                            action={
                                <ReportDateSwitcher
                                    startDate={mbStart}
                                    endDate={mbEnd}
                                    timeframe={mbTimeframe}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    availableTimeframes={[ReportDateTimeframe.Range]}
                                    onSwitchTimeframe={tf => setMbTimeframe(tf)}
                                    onChange={(tf, startD, endD) => {
                                        setMbTimeframe(tf);
                                        setMbStart(startD);
                                        setMbEnd(endD);
                                    }}
                                    showGoStart
                                    showGoEnd
                                    hideDropdown
                                />
                            }
                        />

                        <CardContent>
                            <Box height="75vh" px={2}>
                                <MediaSummaryBreakdownTable
                                    channelFrameId={id}
                                    mediaSummaries={data?.data?.data ?? []}
                                    loading={isLoading}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )}
        </>
    );
};

const KV: React.FC<{ title: TranslationPath; value: React.ReactNode }> = ({ title, value }) => (
    <AutoGrid columnGap={1}>
        <Typography variant="body1">
            <strong>{useCommonTranslation(title)}:</strong>
        </Typography>
        <Typography variant="body1" component="div">
            {value}
        </Typography>
    </AutoGrid>
);

export default ChannelFrameDetails;
